import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Box, Alert, CircularProgress } from "@mui/material";
import FormTextField from "../form/FormTextField";
import FormSelect from "components/form/FormSelect";
import StyledButton from "../button/StyledButton";
import { useCreateFirmMutation, useUpdateFirmMutation, useCreateUserMutation, useDeleteFirmMutation } from "api/api";
import { Firm } from "types/types";
import countriesAndStates from 'assets/countries_and_states.json'
import { getErrorMessage } from "lib/utils/getErrorMessage";
import { getRegionsForCountry } from "pages/AddEcomPage/helpers";

const createFirmSchema = z.object({
  firmName: z
    .string({ required_error: "Firm Name is required" })
    .min(1, { message: "Firm Name is required" }),
  website: z
    .string({ required_error: "Website is required" })
    .min(1, { message: "Website is required" }),
  mainOfficeAddress: z
    .string({ required_error: "Main Office Address is required" })
    .min(1, { message: "Main Office Address is required" }),
  city: z
    .string({ required_error: "City is required" })
    .min(1, { message: "City is required" }),
  region: z
    .string({ required_error: "State / Province / Region is required" })
    .min(1, { message: "State / Province / Region is required" }),
  country: z
    .string({ required_error: "Country is required" })
    .min(1, { message: "Country is required" }),
  zipCode: z
    .string({ required_error: "Zip Code is required" })
    .min(1, { message: "Zip Code is required" }),
  adminFirstName: z
    .string({ required_error: "Admin First Name is required" })
    .min(1, { message: "Admin First Name is required" }),
  adminLastName: z
    .string({ required_error: "Admin Last Name is required" })
    .min(1, { message: "Admin Last Name is required" }),
  adminEmail: z
    .string({ required_error: "Admin email is required" })
    .email({ message: "Invalid email format" }),
});

const editFirmSchema = createFirmSchema.omit({
  adminFirstName: true,
  adminLastName: true,
  adminEmail: true,
});

interface FirmFormProps {
  initialData?: Firm & { id: string };
  onSuccess?: (newFirmName?, newFirmAdminName?) => void;
  onClose?: () => void;
}

const FirmForm: React.FC<FirmFormProps> = ({ initialData, onSuccess, onClose }) => {
  const [createUser, { error: createUserError, isLoading: isCreatingUser }] = useCreateUserMutation();
  const [createFirm, { error: createFirmError, isLoading: isCreatingFirm }] = useCreateFirmMutation();
  const [updateFirm, { error: updateFirmError, isLoading: isUpdatingFirm }] = useUpdateFirmMutation();
  const [deleteFirm, { error: deleteFirmError, isLoading: isDeletingFirm }] = useDeleteFirmMutation(); // only used for error cleanup

  const isEditing = !!initialData?.name;

  const error = isEditing
    ? updateFirmError
    : (createFirmError || createUserError || deleteFirmError);

  const isLoading = isEditing
    ? isUpdatingFirm
    : (isCreatingFirm || isCreatingUser || isDeletingFirm);

  const form = useForm({
    resolver: zodResolver(isEditing ? editFirmSchema : createFirmSchema),
    defaultValues: {
      firmName: initialData?.name || "",
      website: initialData?.website || "",
      mainOfficeAddress: initialData?.address || "",
      city: initialData?.city || "",
      region: initialData?.subnational || "",
      country: initialData?.country || "",
      zipCode: initialData?.zipcode || "",
      adminFirstName: "",
      adminLastName: "",
      adminEmail: "",
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const selectedCountry = watch("country");

  const firmFields = [
    { name: "firmName", label: "Firm Name", component: "FormTextField" },
    { name: "website", label: "Website", component: "FormTextField" },
    { name: "country", label: "Country", component: "FormSelect", menuItems: countriesAndStates.map(c => c.name) },
    { name: "mainOfficeAddress", label: "Main Office Address", component: "FormTextField" },
    { name: "city", label: "City", component: "FormTextField" },
    {
      name: "region", label: "State / Province / Region", component: "FormSelect", menuItems: selectedCountry ?
        getRegionsForCountry(selectedCountry)
        : []
    },
    { name: "zipCode", label: "Zip Code", component: "FormTextField" },
  ] as const;

  const firmAdminFields = [
    { name: "adminFirstName", label: "First Name" },
    { name: "adminLastName", label: "Last Name" },
    { name: "adminEmail", label: "Email" },
  ] as const;

  const submitFirmUpdate = async (formData) => {
    try {
      if (!initialData?.id) throw 'no firm id to update';

      await updateFirm(
        {
          id: initialData.id,
          formData: {
            name: formData.firmName,
            address: formData.mainOfficeAddress,
            country: formData.country,
            subnational: formData.region,
            city: formData.city,
            zipcode: formData.zipCode,
            website: formData.website,
          }
        }
      ).unwrap();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error during firm update:", error);
    }
  }

  const submitFirmCreation = async (formData) => {
    let newFirmId: number | string | undefined;
    try {
      // First create the firm. This will fail if the firm name is already in use, and the firm admin will not be created.
      const firmResponse = await createFirm(
        {
          name: formData.firmName,
          address: formData.mainOfficeAddress,
          country: formData.country,
          subnational: formData.region,
          city: formData.city,
          zipcode: formData.zipCode,
          website: formData.website,
        }
      ).unwrap() as any;
      newFirmId = firmResponse?.id;

      // Then create the firm admin. This will fail if a user with this email address already exists.
      await createUser(
        {
          firm: formData.firmName,
          address: formData.mainOfficeAddress,
          country: formData.country,
          subnational: formData.region,
          city: formData.city,
          zipcode: formData.zipCode,
          site_role: "Firm Admin",
          email: formData.adminEmail,
          first_name: formData.adminFirstName,
          last_name: formData.adminLastName,
        }
      ).unwrap();

      if (onSuccess) {
        const fullName = formData.adminFirstName + " " + formData.adminLastName;
        onSuccess(formData.firmName, fullName);
      }
    } catch (error) {
      console.error("Error during firm creation:", error);

      // newFirmId will be non-null if the firm was created but the firm admin creation failed
      if (newFirmId) {
        try {
          // delete the newly created faulty firm
          await deleteFirm(`${newFirmId}`);
        } catch (e) {
          console.error("Error during firm cleanup:", e);
        }
      }
    }
  }

  const onSubmit = async (formData) => {
    if (isEditing) {
      await submitFirmUpdate(formData);
    } else {
      await submitFirmCreation(formData);
    }
  };

  return (
    <form id="firm-form" onSubmit={handleSubmit(onSubmit)}>
      {firmFields.map((inputLabel) => (
        <Controller
          key={inputLabel.name}
          name={inputLabel.name}
          control={control}
          render={({ field }) => (
            <Box>
              {
                inputLabel.component === "FormSelect" ? (
                  <FormSelect
                    {...field}
                    label={inputLabel.label}
                    error={!!errors[inputLabel.name]}
                    helperText={errors[inputLabel.name]?.message}
                    menuItems={inputLabel.menuItems as unknown as string[]}
                    disabled={isSubmitting || (inputLabel.name === 'region' && !selectedCountry) || isLoading}
                  />
                ) : (
                  <FormTextField
                    {...field}
                    label={inputLabel.label}
                    error={!!errors[inputLabel.name]}
                    helperText={errors[inputLabel.name]?.message}
                    disabled={isSubmitting || isLoading}
                  />
                )}
            </Box>
          )}
        />
      ))}
      {!isEditing && (
        <>
          <div style={{ width: '100%', height: '100%', marginTop: '20px' }}>
            <span style={{ color: 'black', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '700', wordWrap: 'break-word' }}>Firm Admin:</span>
            <span style={{ color: 'black', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word' }}> This first firm admin is mandatory, and will act as the approver of new user signups for this firm. </span>
          </div>
          {firmAdminFields.map(({ name, label }) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <Box>
                  <FormTextField
                    {...field}
                    label={label}
                    error={!!errors[name]}
                    helperText={errors[name]?.message}
                    disabled={isSubmitting || isLoading}
                  />
                </Box>
              )}
            />
          ))}
        </>
      )}
      <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start", mb: 1 }}>
        <StyledButton label="Cancel" type="outlined" onClick={onClose} />
        <StyledButton
          form="firm-form"
          label={isEditing ? "Update Firm" : "Add New Firm"}
          type="filled"
          disabled={isLoading}
          endAdornment={isLoading && (
            <CircularProgress
              size={16}
              sx={{ color: '#fff' }}
            />
          )}
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
    </form>
  );
};

export default FirmForm;